import type {
  Subscription,
  SubscriptionDeviceModel,
  SubscriptionDeviceType,
  SubscriptionModule,
  SubscriptionModuleStatus,
} from "../domain";
import { deriveSubscriptionStatus } from "../data";

export const deviceTypeFixtures: readonly SubscriptionDeviceType[] = [
  { id: "device-type-01", nameEn: "Fuel Level", nameAr: "مستوى الوقود" },
  { id: "device-type-02", nameEn: "GPS Tracker", nameAr: "متتبع GPS" },
  {
    id: "device-type-03",
    nameEn: "Temperature Sensor",
    nameAr: "حساس حرارة",
  },
];

export const deviceModelFixtures: readonly SubscriptionDeviceModel[] = [
  {
    id: "device-model-01",
    deviceTypeId: "device-type-01",
    nameEn: "FLS-100 Standard",
    nameAr: "مقياس وقود قياسي FLS-100",
  },
  {
    id: "device-model-02",
    deviceTypeId: "device-type-01",
    nameEn: "FLS-200 Advanced",
    nameAr: "مقياس وقود متقدم FLS-200",
  },
  {
    id: "device-model-03",
    deviceTypeId: "device-type-02",
    nameEn: "GPS-4G Ultra",
    nameAr: "متتبع GPS جيل رابع فائق",
  },
  {
    id: "device-model-04",
    deviceTypeId: "device-type-02",
    nameEn: "GPS-OBD Plug",
    nameAr: "متتبع GPS موصل منفذ OBD",
  },
  {
    id: "device-model-05",
    deviceTypeId: "device-type-02",
    nameEn: "GPS-Solar Heavy",
    nameAr: "متتبع GPS طاقة شمسية شاق",
  },
  {
    id: "device-model-06",
    deviceTypeId: "device-type-03",
    nameEn: "TEMP-Ble Wireless",
    nameAr: "حساس حرارة بلوتوث لاسلكي",
  },
  {
    id: "device-model-07",
    deviceTypeId: "device-type-03",
    nameEn: "TEMP-Wired Probe",
    nameAr: "حساس حرارة مسبار سلكي",
  },
];

const statuses: readonly SubscriptionModuleStatus[] = [
  "ACTIVE",
  "INACTIVE",
  "EXPIRED",
  "SCHEDULED",
];

export const subscriptionFixtures: readonly Subscription[] = Array.from(
  { length: 28 },
  (_, offset) => {
    const index = offset + 1;
    const modules: SubscriptionModule[] = Array.from(
      // Every seventh holds five modules, past the list's three chips.
      { length: index % 7 === 2 ? 5 : (index % 3) + 1 },
      (_, moduleOffset) => {
        const moduleIndex = ((index + moduleOffset) % 28) + 1;
        const moduleIdStr = `module-${String(moduleIndex).padStart(2, "0")}`;
        const status =
          statuses[(offset + moduleOffset) % statuses.length] ?? "ACTIVE";
        // "No Period" (both dates null) on every 5th line -- exercises the
        // client's handling of a never-expiring module.
        const noPeriod = (index + moduleOffset) % 5 === 0;
        return {
          id: `subscription-module-${index}-${moduleOffset + 1}`,
          moduleId: moduleIdStr,
          module: {
            id: moduleIdStr,
            slug: moduleIdStr,
            nameEn: `Module ${String(moduleIndex).padStart(2, "0")}`,
            nameAr: `وحدة ${String(moduleIndex).padStart(2, "0")}`,
            descriptionEn: `Sellable module ${String(moduleIndex).padStart(2, "0")}.`,
            descriptionAr: `الوحدة القابلة للبيع رقم ${String(moduleIndex).padStart(2, "0")}.`,
          },
          purchasingPrice: 100 + index + moduleOffset,
          startDate: noPeriod ? null : "2026-01-01T00:00:00.000Z",
          endDate: noPeriod ? null : "2026-12-31T23:59:59.999Z",
          status,
        };
      },
    );

    const devices =
      index % 3 === 0
        ? Array.from({ length: (index % 2) + 1 }, (_, deviceOffset) => {
            const typeIndex =
              (offset + deviceOffset) % deviceTypeFixtures.length;
            const deviceType =
              deviceTypeFixtures[typeIndex] ?? deviceTypeFixtures[0]!;
            const availableModels = deviceModelFixtures.filter(
              (model) => model.deviceTypeId === deviceType.id,
            );
            const modelIndex = (offset + deviceOffset) % availableModels.length;
            const deviceModel =
              availableModels[modelIndex] ?? availableModels[0]!;
            return {
              id: `subscription-device-${index}-${deviceOffset + 1}`,
              deviceTypeId: deviceType.id,
              deviceTypeNameEn: deviceType.nameEn,
              deviceTypeNameAr: deviceType.nameAr,
              deviceModelId: deviceModel.id,
              deviceModelNameEn: deviceModel.nameEn,
              deviceModelNameAr: deviceModel.nameAr,
              quantity: ((index + deviceOffset) % 5) + 1,
              duration: "YEARLY" as const,
            };
          })
        : [];

    return {
      id: `subscription-${String(index).padStart(2, "0")}`,
      subscriberId: `company-${String((index % 8) + 1).padStart(2, "0")}`,
      subscriber: {
        id: `company-${String((index % 8) + 1).padStart(2, "0")}`,
        nameEn: `Subscriber ${String((index % 8) + 1).padStart(2, "0")}`,
        nameAr: `مشترك ${String((index % 8) + 1).padStart(2, "0")}`,
      },
      modules,
      devices,
      totalSubscriptionPrice: modules.reduce(
        (sum, module) => sum + module.purchasingPrice,
        0,
      ),
      status: deriveSubscriptionStatus(modules),
      createdAt: `2026-02-${String((index % 28) + 1).padStart(2, "0")}T09:00:00.000Z`,
      updatedAt: `2026-02-${String((index % 28) + 1).padStart(2, "0")}T10:00:00.000Z`,
    };
  },
);
